import { useMemo } from "react";
import { CHECK_TYPE_LABEL } from "./consts";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useGetSystemChecklist, useGetValidationChecklist, useUpdateValidationChecklistMutation } from "@/services";
import { ValidationItem } from "./types";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { actionsKeys, getBaseRoute, permissionActions } from "@/lib/utils";
import { useLocation } from "react-router-dom";

const useValidationCockpit = ({setIsValidationCompleted}: {setIsValidationCompleted?: React.Dispatch<React.SetStateAction<boolean>>}) => {
  const queryClient = useQueryClient();
  const { communityData, currentFinanceYear, fiscalYearDtl, accessPermission } = useSelector((state:RootState) => state.currentData)
  const communityId = communityData?.communityId;
  const basePath = getBaseRoute(useLocation().pathname);
  const isCam = basePath?.toLowerCase() == "cam";
  const isOwner = basePath?.toLowerCase() == "owner";

  const hasEditAccess = useMemo(() => {
    return isCam || isOwner ? accessPermission?.[actionsKeys?.FISCAL_YEAR?.toLowerCase()]?.[permissionActions.UPDATE] : true
  }, [accessPermission]);

  const isLocked = useMemo(() => {
    if (fiscalYearDtl && fiscalYearDtl?.fiscalYear == currentFinanceYear) {
      return fiscalYearDtl?.status?.toLowerCase() == "locked"
    }
    return false;
  }, [fiscalYearDtl]);

  const {
    data: checklistData,
    isLoading: checklistLoading,
  } = useGetValidationChecklist({
    communityId: communityId,
    fiscalYear: currentFinanceYear,
    queryParams: {
      enabled: !!(communityId && currentFinanceYear)
    }
  });

  const {
    data: systemChecksData,
    isLoading: systemLoading,
  } = useGetSystemChecklist({
    communityId: communityId,
    fiscalYear: currentFinanceYear,
    queryParams: {
      enabled: !!(communityId && currentFinanceYear)
    }
  });

  const { mutate: updateValidation, isPending: isUpdateValidationPending } =
    useUpdateValidationChecklistMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Checks Updated Successfully");
        queryClient.invalidateQueries({
          queryKey: ["validation-checklist", communityId, currentFinanceYear],
        });
      },
    });

  const manualValidations: ValidationItem[] = useMemo(() => {
    return (checklistData as any)?.data?.map((item: any) => ({
      id: item.fiscalYearUserChecklistId,
      title: CHECK_TYPE_LABEL[item.checkType] || item.checkType,
      checked: item.isChecked,
      fiscalYearUserChecklistId: item.fiscalYearUserChecklistId,
      checkType: item.checkType,
      status: item.isChecked ? "Passed" : "Pending",
    }))?.sort((a: any, b:any) => a.id - b.id);
  }, [checklistData]);


  const systemValidations: ValidationItem[] = useMemo(() => {
    return (systemChecksData as any)?.data?.checks?.map((item: any, index: number) => ({
      id: index + 1,
      title: item.checkName, 
      description: item.message, 
      status:
        item.status === "PASSED" ||
        item.status === "SUCCESS"
          ? "Passed"
          : "Pending",
    }));
  }, [systemChecksData]);

  
  const toggleValidation = (id: number) => {
    const changedItem = (checklistData as any)?.data?.find(
      (item: any) =>
        item.fiscalYearUserChecklistId === id
    );

    if (!changedItem) return;

    updateValidation({
      communityId: communityData?.communityId,
      fiscalYear: currentFinanceYear,
      checkType: changedItem.checkType,
      data: {
        isChecked: !changedItem.isChecked,
      },
    });
  };


  const progress = useMemo(() => {
    const total = Number(systemValidations?.length || 0) + Number(manualValidations?.length || 0);

    if (!total) return 0;

    const systemPassed =
      systemValidations?.filter(
        (v) => v.status === "Passed"
      ).length || 0;

    const manualPassed =
      manualValidations?.filter(
        (v) => v.checked
      ).length || 0;

      const _progress = Math.round(
        ((systemPassed + manualPassed) / total) * 100
      )
      setIsValidationCompleted?.(_progress == 100)
    return _progress;
  }, [systemValidations, manualValidations]);

  return {
    leftValidations: systemValidations,
    rightValidations: manualValidations,
    toggleValidation,
    progress,
    isLoading: checklistLoading || systemLoading,
    isLocked,
    isUpdateValidationPending,
    hasEditAccess
  };
};

export default useValidationCockpit;